import { cn } from '@repo/ui/lib/utils'
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          theme?: 'auto' | 'light' | 'dark'
          size?: 'normal' | 'flexible' | 'compact'
          execution?: 'render' | 'execute'
          appearance?: 'always' | 'execute' | 'interaction-only'
          callback?: (token: string) => void
          'expired-callback'?: () => void
          'timeout-callback'?: () => void
          'error-callback'?: () => void
        }
      ) => string
      execute: (container: string) => void
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

export interface TurnstileWidgetHandle {
  execute: () => Promise<string>
  reset: () => void
}

interface TurnstileWidgetProps {
  siteKey: string
  onTokenChange: (token: string | null) => void
  className?: string
}

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script'
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

function loadTurnstileScript({ onReady, onError }: { onReady: () => void; onError: () => void }) {
  if (typeof window === 'undefined') {
    return () => undefined
  }

  if (window.turnstile) {
    onReady()
    return () => undefined
  }

  let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null
  const handleLoad = () => onReady()
  const handleError = () => onError()

  if (!script) {
    script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }

  script.addEventListener('load', handleLoad)
  script.addEventListener('error', handleError)

  return () => {
    script?.removeEventListener('load', handleLoad)
    script?.removeEventListener('error', handleError)
  }
}

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ siteKey, onTokenChange, className }: TurnstileWidgetProps, ref) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const widgetIdRef = useRef<string | null>(null)
    const pendingResolverRef = useRef<((token: string) => void) | null>(null)
    const pendingRejectorRef = useRef<((error: Error) => void) | null>(null)
    const latestTokenRef = useRef<string | null>(null)
    const [scriptReady, setScriptReady] = useState(
      typeof window !== 'undefined' && typeof window.turnstile !== 'undefined'
    )
    const [scriptError, setScriptError] = useState<string | null>(null)
    const containerId = useId().replace(/:/g, '')

    const rejectPending = useCallback((message: string) => {
      pendingRejectorRef.current?.(new Error(message))
      pendingResolverRef.current = null
      pendingRejectorRef.current = null
    }, [])

    const resetWidget = useCallback(() => {
      latestTokenRef.current = null
      onTokenChange(null)

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current)
      }
    }, [onTokenChange])

    useImperativeHandle(
      ref,
      () => ({
        execute: () => {
          if (latestTokenRef.current) {
            return Promise.resolve(latestTokenRef.current)
          }

          if (!scriptReady || !window.turnstile || !widgetIdRef.current) {
            const message = 'Unable to load the security check. Disable blockers and retry.'
            setScriptError(message)
            return Promise.reject(new Error(message))
          }

          resetWidget()
          setScriptError(null)

          return new Promise<string>((resolve, reject) => {
            pendingResolverRef.current = resolve
            pendingRejectorRef.current = reject
            window.turnstile?.execute(`#${containerId}`)
          })
        },
        reset: () => {
          rejectPending('Security check reset before completion.')
          resetWidget()
          setScriptError(null)
        },
      }),
      [containerId, rejectPending, resetWidget, scriptReady]
    )

    useEffect(() => {
      latestTokenRef.current = null
      onTokenChange(null)

      return loadTurnstileScript({
        onReady: () => {
          setScriptReady(true)
          setScriptError(null)
        },
        onError: () => {
          const message = 'Unable to load the security check. Disable blockers and retry.'
          setScriptError(message)
          rejectPending(message)
          onTokenChange(null)
        },
      })
    }, [onTokenChange, rejectPending])

    useEffect(() => {
      if (!scriptReady || scriptError || !containerRef.current || !window.turnstile) {
        return
      }

      latestTokenRef.current = null
      onTokenChange(null)
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'auto',
        size: 'flexible',
        execution: 'execute',
        appearance: 'interaction-only',
        callback: (token) => {
          latestTokenRef.current = token
          setScriptError(null)
          onTokenChange(token)
          pendingResolverRef.current?.(token)
          pendingResolverRef.current = null
          pendingRejectorRef.current = null
        },
        'expired-callback': () => {
          latestTokenRef.current = null
          onTokenChange(null)
          rejectPending('Security check expired. Please try again.')
        },
        'timeout-callback': () => {
          latestTokenRef.current = null
          onTokenChange(null)
          rejectPending('Security check timed out. Please try again.')
        },
        'error-callback': () => {
          const message = 'Security check failed. Please retry.'
          latestTokenRef.current = null
          setScriptError(message)
          onTokenChange(null)
          rejectPending(message)
        },
      })

      return () => {
        rejectPending('Security check was interrupted.')
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
        }
      }
    }, [onTokenChange, rejectPending, scriptError, scriptReady, siteKey])

    return (
      <div className={cn('space-y-1.5', className)}>
        <div id={containerId} ref={containerRef} className="min-h-0 overflow-visible" />
        {scriptError ? <p className="px-1 text-[11px] text-destructive/90">{scriptError}</p> : null}
      </div>
    )
  }
)
